import { getBaseUrl } from '../../base/util/helpers';
import { showWarningNotification } from '../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../../notifications/constants';
import { timeout } from '../../virtual-background/functions';
import logger from '../../virtual-background/logger';

import MHStreamBackgroundEffect from './MHStreamBackgroundEffect';
import {
    formatDeviceCapabilityInfo,
    getAdaptiveWasmTimeout,
    getDeviceCapabilityInfo,
    getQualitySettings,
    getSegmentationResolution,
    hasEnoughMemory
} from './deviceCapabilities';
import PerformanceMonitor, {
    validateDeviceCapabilities
} from './performanceMonitor';
import createTFLiteModule from './vendor/tflite/tflite';
import createTFLiteSIMDModule from './vendor/tflite/tflite-simd';

/**
 * Path of the segmentation model relative to the app's base URL. `make deploy-meet-models` copies it into
 * the same `libs/` directory that `app.bundle.min.js` and the tflite `.wasm` binaries are served from, so
 * it always sits next to the bundle that asks for it.
 */
const MODEL_PATH = 'libs/selfie_segmentation_landscape.tflite';

/**
 * Every TensorFlow Lite flatbuffer carries the file identifier `TFL3` at byte offset 4.
 *
 * @see https://github.com/tensorflow/tensorflow/blob/master/tensorflow/lite/schema/schema.fbs
 */
const TFLITE_FILE_IDENTIFIER = 'TFL3';
const TFLITE_FILE_IDENTIFIER_OFFSET = 4;

/**
 * The model is ~250KB. Anything substantially smaller cannot be it, whatever the server said the request
 * status was.
 */
const MIN_PLAUSIBLE_MODEL_BYTES = 1024;

/**
 * How many times a session will try to fetch the model before giving up.
 *
 * The failure modes that actually occur - the model missing from the deployment, a proxy serving
 * something else in its place - do not heal within a session, and every entry point into this module
 * (track creation, the background preview, each toggle, each camera switch) used to start a fresh
 * attempt. That turned one broken deployment into a burst of identical downloads and identical error
 * reports for as long as the user stayed in the conference.
 */
const MAX_MODEL_LOAD_ATTEMPTS = 3;


let modelBuffer;
let modelPromise;
let modelLoadAttempts = 0;
let tflite;
let tflitePromise;
let wasmCheck;
let isWasmDisabled = false;
let deviceCapabilityInfo = null;
let performanceMonitor = null;
let deviceValidation = null;

/**
 * Builds the ordered list of URLs to try for the segmentation model.
 *
 * The URL has to be resolved the same way the browser resolves `<script src="libs/app.bundle.min.js">`
 * in `index.html`, which is against the document's `<base href>` (injected by the `base.html` SSI
 * include). It previously took the first segment of `window.location.pathname` as the app path, on the
 * assumption that the conference is served from a path prefix that appears in the page URL. In this
 * deployment it is not: the app lives under `/conf/` while a conference URL is `https://host/<room>`, so
 * the first path segment is the *room name*. That produced
 * `https://host/MHR260830214707/libs/selfie_segmentation_landscape.tflite` - a URL that either 404s or,
 * worse, is caught by the SPA rewrite and answered with `index.html` under a 200 (see
 * {@link _assertIsTfLiteModel} for why that is worse).
 *
 * Resolving against the base URL is correct for every layout: a path prefix, the document root, and a
 * `<base>` pointing at a CDN - which is where `libs/` is served from when one is configured.
 *
 * @private
 * @returns {Array<string>} Model URLs to try, in order, without duplicates.
 */
function _getModelSources() {
    const sources = [];

    // An explicit override always wins. Read lazily rather than at module scope: this module is
    // evaluated before `APP.store` exists, so a module-level read could never see the config.
    if (typeof APP !== 'undefined' && APP.store) {
        const { assetBaseUrl } = APP.store.getState()['features/base/config'] ?? {};

        if (assetBaseUrl) {
            sources.push(`${assetBaseUrl.replace(/\/+$/, '')}/${MODEL_PATH}`);
        }
    }

    // The same resolution the browser applies to the relative <script> tags in index.html.
    try {
        sources.push(new URL(MODEL_PATH, getBaseUrl()).href);
    } catch (e) {
        logger.warn('[VirtualBackground] Could not resolve the model against the base URL', e);
    }

    // Last resort for a deployment served straight from the document root, or one whose <base> points
    // at a CDN that does not carry the model.
    sources.push(`${window.location.origin}/${MODEL_PATH}`);

    return [ ...new Set(sources) ];
}

/**
 * Rejects a downloaded buffer that is not a TensorFlow Lite model.
 *
 * A request can "succeed" and still not return the model: an SPA rewrite answering an unknown path with
 * `index.html` under a 200, a captive portal, a CDN error page, or a truncated response. `HEAPU8.set()`
 * copies any of those into the WebAssembly heap quite happily, and `_loadModel` then parses the bytes as
 * a flatbuffer - following offsets read out of HTML. That is what raised `RuntimeError: memory access
 * out of bounds` from inside `tflite-simd.wasm`, several frames and one module-level cache away from the
 * bad download that caused it.
 *
 * Checking the flatbuffer's file identifier here turns that trap into a plain, attributable load
 * failure, and keeps the WebAssembly instance clean for the next attempt.
 *
 * @private
 * @param {Uint8Array} bytes - The downloaded payload.
 * @param {string} source - The URL it came from, for the error message.
 * @returns {void}
 */
function _assertIsTfLiteModel(bytes, source) {
    if (bytes.byteLength < MIN_PLAUSIBLE_MODEL_BYTES) {
        throw new Error(`Segmentation model from ${source} is too small to be the model: `
            + `${bytes.byteLength} bytes`);
    }

    const identifier = String.fromCharCode(
        ...bytes.subarray(TFLITE_FILE_IDENTIFIER_OFFSET, TFLITE_FILE_IDENTIFIER_OFFSET + 4));

    if (identifier !== TFLITE_FILE_IDENTIFIER) {
        throw new Error(`Response from ${source} is not a TensorFlow Lite model `
            + `(expected identifier ${TFLITE_FILE_IDENTIFIER}, got ${JSON.stringify(identifier)}); `
            + 'the request most likely returned an error page or a redirect body');
    }
}

/**
 * Discards the cached WebAssembly module and model.
 *
 * A WebAssembly RuntimeError ("memory access out of bounds") traps the instance: its linear memory is
 * left in an undefined state and every later call into it can fail the same way. Because `tflite` is a
 * module-level singleton reused by every subsequent attempt, keeping it after a trap meant one bad load
 * disabled virtual backgrounds for the rest of the session - retrying, switching backgrounds and even
 * rejoining the conference all reused the same poisoned module. Dropping it costs one re-download and
 * lets the next attempt start from a clean instance.
 *
 * @private
 * @returns {void}
 */
function _discardTfliteInstance() {
    tflite = undefined;
    tflitePromise = undefined;
    modelBuffer = undefined;

    // The model lives inside the discarded instance's heap, so the record of having loaded it goes with
    // it. Leaving `modelPromise` behind would hand the next attempt a buffer that was written into a
    // module that no longer exists.
    modelPromise = undefined;
}

/**
 * Downloads the segmentation model and loads it into the WebAssembly heap.
 *
 * Kept separate from {@link createVirtualBackgroundEffect} so the work can be shared by concurrent
 * callers. There are several independent entry points - `loadEffects` builds the effect alongside the
 * local track, the settings dialog builds one for its preview, and each toggle builds another - and they
 * overlap routinely. Each used to run this block on its own, and because the module-level `modelBuffer`
 * is only assigned at the very end, they all saw it unset and all proceeded: several downloads of the
 * same file, then interleaved `HEAPU8.set()` writes to the *same* heap offset followed by concurrent
 * `_loadModel` calls. A model being overwritten while tflite parses it is a second, self-inflicted way
 * to reach `memory access out of bounds`.
 *
 * @private
 * @returns {Promise<ArrayBuffer>} Resolves with the model buffer once it is loaded into tflite.
 */
async function _loadSegmentationModel() {
    const { fetchAxiosApi } = require('../../base/mtApi/functions');
    const modelSources = _getModelSources();
    const failures = [];

    let buffer = null;
    let loadedFrom = null;

    for (const modelSource of modelSources) {
        logger.debug(`[VirtualBackground] Attempting to load model from: ${modelSource}`);

        // `fetchAxiosApi` resolves with the error instead of rejecting, so a failed request arrives here
        // as a value, not as an exception - a `try`/`catch` around it never fires. Detect it explicitly;
        // without this the only symptom of, say, a 404 was the generic "failed from any source" message
        // with no status and no indication of which URL was wrong.
        const response = await fetchAxiosApi(modelSource, { responseType: 'arraybuffer' });

        try {
            if (response instanceof Error) {
                throw response;
            }

            _assertIsTfLiteModel(new Uint8Array(response ?? new ArrayBuffer(0)), modelSource);

            buffer = response;
            loadedFrom = modelSource;
            break;
        } catch (err) {
            failures.push(`${modelSource}: ${err.message}`);
        }
    }

    if (!buffer) {
        throw new Error(`Failed to load the segmentation model. Tried ${
            modelSources.length} source(s): ${failures.join(' | ')}`);
    }

    logger.info(`[VirtualBackground] Successfully loaded model from: ${loadedFrom}`);

    // Re-checked after the awaits above: a concurrent failure can have discarded the module while this
    // download was in flight, and dereferencing it then threw
    // "Cannot read properties of undefined (reading '_getModelBufferMemoryOffset')".
    if (!tflite) {
        throw new Error('WebAssembly tflite module was discarded while the model was downloading');
    }

    const modelBytes = new Uint8Array(buffer);
    const offset = tflite._getModelBufferMemoryOffset();

    if (offset + modelBytes.byteLength > tflite.HEAPU8.byteLength) {
        throw new Error(`Segmentation model does not fit in the tflite heap: ${
            modelBytes.byteLength} bytes at offset ${offset}, heap is ${tflite.HEAPU8.byteLength}`);
    }

    tflite.HEAPU8.set(modelBytes, offset);
    tflite._loadModel(modelBytes.byteLength);

    logger.info('[VirtualBackground] Model loaded successfully into TensorFlow Lite');

    return buffer;
}

/**
 * Creates a new instance of MHStreamBackgroundEffect. This loads the Meet background model that is used to
 * extract person segmentation.
 *
 * @param {Object} virtualBackground - The virtual object that contains the background image source and
 * the isVirtualBackground flag that indicates if virtual image is activated.
 * @param {string} [virtualBackground.backgroundType] - Type of background.
 * @param {number} [virtualBackground.blurValue] - Blur value for background.
 * @param {string} [virtualBackground.virtualSource] - Source for virtual background.
 * @param {Function} [dispatch] - The Redux dispatch function.
 * @returns {Promise<MHStreamBackgroundEffect|undefined>}
 */
export async function createVirtualBackgroundEffect(virtualBackground, dispatch) {
    if (!MediaStreamTrack.prototype.getSettings && !MediaStreamTrack.prototype.getConstraints) {
        throw new Error('MHStreamBackgroundEffect not supported!');
    }

    // Detect device capabilities once at start
    if (!deviceCapabilityInfo) {
        deviceCapabilityInfo = getDeviceCapabilityInfo();
        logger.info(`[VirtualBackground] Device Capability: ${formatDeviceCapabilityInfo()}`);

        // Initialize performance monitor
        performanceMonitor = new PerformanceMonitor();

        // Validate capabilities accounting for video conference overhead
        logger.debug('[VirtualBackground] Validating device capabilities with video conference load...');
        try {
            deviceValidation = await validateDeviceCapabilities(deviceCapabilityInfo);

            if (!deviceValidation.isValid) {
                logger.error('[VirtualBackground] Device validation failed:', deviceValidation.issues);
                if (dispatch && deviceValidation.issues.length > 0) {
                    dispatch(showWarningNotification({
                        titleKey: 'virtualBackground.deviceNotSupported',
                        descriptionKey: 'virtualBackground.deviceNotSupportedDescription'
                    }, NOTIFICATION_TIMEOUT_TYPE.LONG));
                }

                return;
            }

            // Log validation warnings
            if (deviceValidation.warnings.length > 0) {
                deviceValidation.warnings.forEach(warning => {
                    logger.warn(`[VirtualBackground] ${warning}`);
                });
            }

            // Downgrade capability if needed based on actual performance
            if (deviceValidation.recommendedCapability !== deviceCapabilityInfo.capability) {
                logger.info('[VirtualBackground] Capability downgraded: '
                    + `${deviceCapabilityInfo.capability} → ${deviceValidation.recommendedCapability} `
                    + `(video conference overhead: ${deviceValidation.performanceProfile.videoConferenceOverhead.toFixed(1)}%)`);
            }
        } catch (e) {
            logger.error('[VirtualBackground] Capability validation error', e);

            // Continue anyway with detected capability
        }
    }

    // Warn if insufficient memory
    if (!hasEnoughMemory()) {
        logger.warn('[VirtualBackground] Low memory device detected (<4GB). Virtual background may cause jitter.');
        if (dispatch) {
            dispatch(showWarningNotification({
                titleKey: 'virtualBackground.lowMemoryWarning',
                descriptionKey: 'virtualBackground.lowMemoryWarningDescription'
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        }
    }

    if (isWasmDisabled) {
        if (dispatch) {
            dispatch(showWarningNotification({
                titleKey: 'virtualBackground.backgroundEffectError',
                descriptionKey: 'virtualBackground.backgroundEffectErrorDes'
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        }

        return;
    }

    // Checks if WebAssembly feature is supported or enabled by/in the browser.
    // Conditional import of wasm-check package is done to prevent
    // the browser from crashing when the user opens the app.

    if (!tflite) {
        try {
            wasmCheck = require('wasm-check');

            // Keep the in-flight module creation around. The timeout below abandons the wait, not the download, so
            // starting over on the next attempt threw away a partly downloaded multi-MB wasm module and gave the
            // user another 10s wait on the slow connection that caused the first timeout. Reusing the promise lets
            // a slow load finish in the background and be ready for the next attempt.
            if (!tflitePromise) {
                // Prefer the SIMD build - it is the same tflite/XNNPACK kernels compiled with WebAssembly SIMD,
                // and it is several times faster on the segmentation model.
                //
                // wasm-check reports whether the *browser* advertises SIMD, which is not the same as the module
                // instantiating successfully: a CSP that blocks wasm-eval, a proxy that truncates the .wasm, or
                // one of the Safari builds with partial SIMD all satisfy the feature check and then fail at
                // instantiation. That failure used to fall straight into the catch below, which sets
                // isWasmDisabled and turns the virtual background off for the whole session - even though the
                // plain build would have run perfectly well. Try it before giving up.
                const preferSimd = Boolean(wasmCheck?.feature?.simd);

                tflitePromise = (preferSimd ? createTFLiteSIMDModule() : createTFLiteModule())
                    .catch(err => {
                        if (!preferSimd) {
                            throw err;
                        }

                        logger.warn('The SIMD tflite build failed to initialise; falling back to the plain '
                            + 'WebAssembly build', err);

                        return createTFLiteModule();
                    });

                // A handler is attached here and not chained onto tflitePromise so that a rejection arriving
                // after this call already timed out is still handled, instead of surfacing as an unhandled
                // promise rejection. Only a real failure invalidates the cached attempt; a timeout keeps waiting.
                tflitePromise.then(
                    () => { /* awaited below (and by later calls) */ },
                    () => {
                        tflitePromise = undefined;
                    });
            }

            const adaptiveTimeout = getAdaptiveWasmTimeout();

            logger.debug(`[VirtualBackground] Loading WASM with ${adaptiveTimeout}ms timeout`);
            tflite = await timeout(adaptiveTimeout, tflitePromise);
        } catch (err) {
            if (err && err.message === '408') {
                logger.warn('Timed out waiting for the tflite model; it keeps loading in the background.');
                if (dispatch) {
                    dispatch(showWarningNotification({
                        titleKey: 'virtualBackground.backgroundEffectError',
                        descriptionKey: 'virtualBackground.backgroundEffectErrorDes'
                    }, NOTIFICATION_TIMEOUT_TYPE.LONG));
                }
            } else {
                isWasmDisabled = true;
                logger.error('Looks like WebAssembly is disabled or not supported on this browser', err);
                if (dispatch) {
                    dispatch(showWarningNotification({
                        titleKey: 'virtualBackground.webAssemblyWarning'
                    }, NOTIFICATION_TIMEOUT_TYPE.LONG));
                }
            }

            return;
        }
    }

    if (!modelBuffer) {
        if (modelLoadAttempts >= MAX_MODEL_LOAD_ATTEMPTS) {
            logger.warn(`[VirtualBackground] Giving up on the segmentation model: ${modelLoadAttempts} `
                + 'load attempts in this session, none of which left a usable model');

            if (dispatch) {
                dispatch(showWarningNotification({
                    titleKey: 'virtualBackground.backgroundEffectError',
                    descriptionKey: 'virtualBackground.backgroundEffectErrorDes'
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }

            return;
        }

        if (!modelPromise) {
            modelLoadAttempts++;
            modelPromise = _loadSegmentationModel();

            // Attached here rather than chained so that a rejection is handled even for callers that
            // joined after this one, and never surfaces as an unhandled rejection.
            modelPromise.catch(() => {
                modelPromise = undefined;
            });
        }

        try {
            // Assigned only once the model is actually in tflite. Assigning before the load meant a
            // failed load still marked the model as ready, and every later call skipped this block and
            // ran segmentation against a model that was never there.
            modelBuffer = await modelPromise;
        } catch (error) {
            logger.error('Failed to load the segmentation model from any source', error);

            // A trapped module cannot be reused, and a partially written one is worse than none.
            _discardTfliteInstance();

            if (dispatch) {
                dispatch(showWarningNotification({
                    titleKey: 'virtualBackground.backgroundEffectError',
                    descriptionKey: 'virtualBackground.backgroundEffectErrorDes'
                }, NOTIFICATION_TIMEOUT_TYPE.LONG));
            }

            return;
        }
    }

    // Both module-level singletons can have been dropped by a concurrent caller between the awaits above
    // and here - `_discardTfliteInstance()` is reached from three different failure paths. Handing an
    // undefined module to the effect constructor produced a confusing secondary failure inside
    // MHStreamBackgroundEffect rather than the load failure it actually is.
    if (!tflite || !modelBuffer) {
        logger.warn('[VirtualBackground] The tflite module was discarded before the effect could be '
            + 'constructed; not building an effect on a module that no longer exists');

        if (dispatch) {
            dispatch(showWarningNotification({
                titleKey: 'virtualBackground.backgroundEffectError',
                descriptionKey: 'virtualBackground.backgroundEffectErrorDes'
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        }

        return;
    }

    // Get adaptive segmentation resolution based on device capability
    // Use recommended capability if validation downgraded it
    const capabilityToUse = deviceValidation?.recommendedCapability || deviceCapabilityInfo.capability;
    const adaptiveResolution = getSegmentationResolution();

    // IMPORTANT: Pass validated/downgraded capability to get correct quality settings
    const qualitySettings = getQualitySettings(capabilityToUse);

    const options = {
        width: adaptiveResolution.width,
        height: adaptiveResolution.height,
        virtualBackground,
        qualitySettings,
        performanceMonitor, // Pass monitor for runtime tracking
        capabilityUsed: capabilityToUse
    };

    try {
        const effect = new MHStreamBackgroundEffect(tflite, options);

        // Start performance monitoring after effect is created
        if (performanceMonitor) {
            performanceMonitor.startMonitoring();
            logger.debug('[VirtualBackground] Performance monitoring started');
        }

        return effect;
    } catch (error) {
        // Constructing the effect allocates against the same WebAssembly heap, so a module trapped by an
        // earlier bad model surfaces here rather than at the load above - which is why this reached the
        // caller as an uncaught "memory access out of bounds" instead of a handled load failure. Drop the
        // instance so the next attempt rebuilds it, and report a failure the caller can act on.
        logger.error('Failed to construct the background effect', error);
        _discardTfliteInstance();

        if (dispatch) {
            dispatch(showWarningNotification({
                titleKey: 'virtualBackground.backgroundEffectError',
                descriptionKey: 'virtualBackground.backgroundEffectErrorDes'
            }, NOTIFICATION_TIMEOUT_TYPE.LONG));
        }
    }
}
